import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useStudyGroupSources,
  useCreateStudyGroup,
  useQuestionsFeed,
  useClassUploads,
  useClassFriendUploads,
  useFriends,
  useMe,
  type StudyGroupSource,
} from '../lib/api';
import { buildCategoryIndex } from '../lib/topic-tags';

type SourceType = 'class_section' | 'general_exam';
type Difficulty = 'easy' | 'hard';
type Speed = 'rapid' | 'slow';
type Scope = 'all' | 'mine' | 'friends';

// Per-question time depends on BOTH difficulty and pace: easy/quick questions are
// shorter, hard MCQs get more time to read and reason.
const TIMER_SECONDS: Record<Difficulty, Record<Speed, number>> = {
  easy: { rapid: 5, slow: 15 },
  hard: { rapid: 10, slow: 20 },
};

export function StudyGroupCreatePage() {
  const navigate = useNavigate();

  // Study groups are Pro-only; bounce non-Pro who deep-link here.
  const me = useMe();
  useEffect(() => {
    if (me.data && me.data.user.proAccess === false) navigate('/study-groups', { replace: true });
  }, [me.data, navigate]);

  const [step, setStep] = useState(1);
  const [difficulty, setDifficulty] = useState<Difficulty | null>(null);
  const [speed, setSpeed] = useState<Speed | null>(null);
  const [source, setSource] = useState<(StudyGroupSource & { type: SourceType }) | null>(null);

  // Question-picker state (mirrors regular study sessions)
  const [scope, setScope] = useState<Scope>('all');
  const [showFriendsUpsell, setShowFriendsUpsell] = useState(false);
  const [selectedUploadIds, setSelectedUploadIds] = useState<Set<string>>(new Set());
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [questionCount, setQuestionCount] = useState(20);

  const questionType: 'mcq' | 'quick_mcq' = difficulty === 'easy' ? 'quick_mcq' : 'mcq';
  const timerSeconds = difficulty && speed ? TIMER_SECONDS[difficulty][speed] : 15;
  const isClass = source?.type === 'class_section';

  const { data: sourcesData, isLoading: sourcesLoading } = useStudyGroupSources();
  const sources: Array<StudyGroupSource & { type: SourceType }> = useMemo(() => {
    if (!sourcesData) return [];
    const classes = (sourcesData.classes ?? []).map((s) => ({ ...s, type: 'class_section' as const }));
    const exams = (sourcesData.exams ?? []).map((s) => ({ ...s, type: 'general_exam' as const }));
    return [...classes, ...exams];
  }, [sourcesData]);

  // Feed for the chosen source (only fetched once a source is picked)
  const { data: feed } = useQuestionsFeed(
    source ? (isClass ? { class_section_id: source.id } : { exam_id: source.id }) : {},
  );
  const { data: friendsData } = useFriends();
  const friendIds = useMemo(
    () => new Set((friendsData?.friends ?? []).filter((f) => f.status === 'accepted').map((f) => f.id)),
    [friendsData],
  );
  const { data: uploadsData } = useClassUploads(isClass && source ? { class_section_id: source.id } : {});
  const { data: friendUploadsData } = useClassFriendUploads(isClass && source ? { class_section_id: source.id } : {});
  const myUploads = uploadsData?.uploads ?? [];
  const friendUploads = friendUploadsData?.uploads ?? [];
  const canUseFriendDecks = feed?.entitlements?.canUseFriendDecks ?? false;

  const createMutation = useCreateStudyGroup();

  // Pool of the chosen type
  const typedPool = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type === questionType),
    [feed?.questions, questionType],
  );

  const applyScope = (pool: any[]): any[] => {
    let p = pool;
    if (scope === 'mine' && feed?.currentUserId) {
      p = p.filter((q: any) => q.authorUserId === feed.currentUserId);
    } else if (scope === 'friends') {
      p = p.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId));
    }
    if (scope !== 'all' && selectedUploadIds.size > 0) {
      p = p.filter((q: any) => q.ingestionJobId && selectedUploadIds.has(q.ingestionJobId));
    }
    return p;
  };

  const scopedPool = useMemo(
    () => applyScope(typedPool),
    [typedPool, scope, feed?.currentUserId, friendIds, selectedUploadIds],
  );

  // Topic index over the scoped pool (mcq uses rich categories; quick uses payload.topic)
  const categoryIndex = useMemo(() => {
    if (questionType === 'mcq') return buildCategoryIndex(scopedPool);
    const idx = new Map<string, Set<string>>();
    for (const q of scopedPool) {
      const topic = (q.payload as any)?.topic || 'General';
      let set = idx.get(topic);
      if (!set) { set = new Set(); idx.set(topic, set); }
      set.add(q.id);
    }
    return idx;
  }, [scopedPool, questionType]);

  const availableTopics = useMemo(
    () => Array.from(categoryIndex.entries()).sort((a, b) => b[1].size - a[1].size).map(([name, ids]) => ({ name, count: ids.size })),
    [categoryIndex],
  );
  const allTopics = selectedTopics.size === 0;

  const filteredPool = useMemo(() => {
    if (allTopics) return scopedPool;
    const ids = new Set<string>();
    for (const t of selectedTopics) categoryIndex.get(t)?.forEach((id) => ids.add(id));
    return scopedPool.filter((q) => ids.has(q.id));
  }, [scopedPool, allTopics, selectedTopics, categoryIndex]);

  const available = filteredPool.length;
  const maxQuestions = Math.min(50, available);
  const effectiveCount = Math.min(Math.max(5, questionCount), Math.max(5, maxQuestions));

  const counts = useMemo(() => ({
    all: typedPool.length,
    mine: typedPool.filter((q: any) => q.authorUserId === feed?.currentUserId).length,
    friends: typedPool.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId)).length,
  }), [typedPool, feed?.currentUserId, friendIds]);

  const toggleUpload = (id: string) =>
    setSelectedUploadIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleTopic = (name: string) =>
    setSelectedTopics((prev) => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const resetPicker = () => { setScope('all'); setSelectedUploadIds(new Set()); setSelectedTopics(new Set()); };

  const handleSubmit = async () => {
    if (!source || !difficulty || !speed) return;
    const candidateQuestionIds = filteredPool.map((q) => q.id);
    if (candidateQuestionIds.length < 5) return;
    try {
      const result = await createMutation.mutateAsync({
        sourceType: source.type,
        sourceId: source.id,
        questionType,
        candidateQuestionIds,
        topicFilter: allTopics ? null : Array.from(selectedTopics),
        questionCount: effectiveCount,
        timerSeconds,
      });
      navigate(`/study-groups/${result.group.id}/lobby`);
    } catch { /* error shown via mutation state */ }
  };

  const handleBack = () => {
    if (step === 1) navigate('/study-groups');
    else setStep(step - 1);
  };

  const activeUploads = scope === 'friends' ? friendUploads : myUploads;
  const uploadCountFor = (u: any) => (questionType === 'quick_mcq' ? u.quickCount : u.questionCount);

  return (
    <div className="max-w-lg mx-auto pb-24 px-4">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={handleBack} className="text-slate-500 hover:text-squishy-dark transition-colors" aria-label="Go back">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-xl font-bold text-squishy-dark">Create Study Group</h1>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${s <= step ? 'bg-squishy-primary text-white' : 'bg-slate-200 text-slate-400'}`}>{s}</div>
            {s < 4 && <div className={`flex-1 h-0.5 rounded ${s < step ? 'bg-squishy-primary' : 'bg-slate-200'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Difficulty */}
      {step === 1 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-squishy-dark">Pick a difficulty</h2>
          <p className="text-xs text-slate-500">Easy is fast recall (Quick Study). Hard is full exam-style questions.</p>
          <ChoiceCard
            title="Easy Questions"
            subtitle="Quick Study: short one-line questions, tap the answer, no explanations."
            emoji="⚡"
            active={difficulty === 'easy'}
            accent="amber"
            onClick={() => { setDifficulty('easy'); resetPicker(); setStep(2); }}
          />
          <ChoiceCard
            title="Hard Questions"
            subtitle="Full multiple-choice questions from your class or exam (MCQ only)."
            emoji={'\u{1F9E0}'}
            active={difficulty === 'hard'}
            accent="teal"
            onClick={() => { setDifficulty('hard'); resetPicker(); setStep(2); }}
          />
        </div>
      )}

      {/* Step 2: Speed */}
      {step === 2 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-squishy-dark">Pick a pace</h2>
          <p className="text-xs text-slate-500">How long does everyone get to answer each question?</p>
          <ChoiceCard
            title="Rapid Fire"
            subtitle={`${difficulty === 'hard' ? 10 : 5} seconds per question. Think fast.`}
            emoji={'\u{1F525}'}
            active={speed === 'rapid'}
            accent="red"
            onClick={() => { setSpeed('rapid'); setStep(3); }}
          />
          <ChoiceCard
            title="Slow Poke"
            subtitle={`${difficulty === 'hard' ? 20 : 15} seconds per question. More time to think.`}
            emoji={'\u{1F422}'}
            active={speed === 'slow'}
            accent="teal"
            onClick={() => { setSpeed('slow'); setStep(3); }}
          />
        </div>
      )}

      {/* Step 3: Source */}
      {step === 3 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-squishy-dark">Choose a class or exam</h2>
          <p className="text-xs text-slate-500">Where should the questions come from?</p>
          {sourcesLoading ? (
            <div className="text-center py-12 text-slate-400 text-sm">Loading...</div>
          ) : sources.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No sources available. Enroll in a class or browse exams first.</div>
          ) : (
            <div className="space-y-2">
              {sources.map((s) => (
                <button
                  key={`${s.type}-${s.id}`}
                  onClick={() => { setSource(s); resetPicker(); setQuestionCount(20); setStep(4); }}
                  className="w-full bg-white border border-slate-200 rounded-xl p-4 text-left transition-colors hover:border-squishy-primary"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-squishy-dark">{s.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.type === 'class_section' ? 'Class' : 'Exam'}</div>
                    </div>
                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 4: Question picker */}
      {step === 4 && source && (
        <div className="space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-squishy-dark">Choose questions</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {difficulty === 'easy' ? 'Quick Study' : 'Hard MCQs'} {'·'} {timerSeconds}s per question {'·'} {source.name}
            </p>
          </div>

          {/* Source tabs */}
          <div className="flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
            {(['all', 'mine', 'friends'] as Scope[]).map((sc) => {
              const locked = sc === 'friends' && !canUseFriendDecks;
              return (
                <button
                  key={sc}
                  onClick={() => {
                    if (locked) { setShowFriendsUpsell(true); return; }
                    setScope(sc); setSelectedUploadIds(new Set()); setSelectedTopics(new Set());
                  }}
                  className={`flex-1 py-1.5 text-[11px] font-semibold rounded-md transition-all inline-flex items-center justify-center gap-1 ${scope === sc ? 'bg-white text-squishy-primary shadow-sm' : 'text-slate-500'} ${locked ? 'opacity-70' : ''}`}
                >
                  {locked && (
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-amber-500">
                      <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
                    </svg>
                  )}
                  {sc === 'all' ? 'All' : sc === 'mine' ? 'My Q' : 'Friends Q'} ({counts[sc]})
                </button>
              );
            })}
          </div>

          {/* Upload chips (class sources only) */}
          {isClass && scope !== 'all' && activeUploads.filter((u) => uploadCountFor(u) > 0).length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                {scope === 'friends' ? "Friends' uploads" : 'From specific uploads'} {selectedUploadIds.size > 0 ? `(${selectedUploadIds.size} selected)` : '(all)'}
              </div>
              <div className="max-h-28 overflow-y-auto space-y-1.5 pr-1">
                {activeUploads.filter((u) => uploadCountFor(u) > 0).map((u) => (
                  <button
                    key={u.id}
                    onClick={() => toggleUpload(u.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-all active:scale-[0.98] ${selectedUploadIds.has(u.id) ? 'border-squishy-primary bg-teal-50 text-squishy-primary' : 'border-slate-200 text-slate-600'}`}
                  >
                    <span className="text-xs font-medium truncate">
                      {scope === 'friends' && u.authorName ? <span className="text-slate-500">{u.authorName} · </span> : null}{u.title}
                      {scope === 'friends' && u.createdAt ? (
                        <span className="text-slate-400 font-normal"> · {new Date(u.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })}</span>
                      ) : null}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0 ml-2">{uploadCountFor(u)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Topics */}
          <div className="space-y-2 max-h-56 overflow-y-auto">
            <button
              onClick={() => setSelectedTopics(new Set())}
              className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border-2 transition-all active:scale-[0.98] ${allTopics ? 'border-squishy-primary bg-teal-50 text-squishy-primary' : 'border-slate-200 text-slate-600'}`}
            >
              <span className="font-medium text-sm">All Topics</span>
              <span className="text-xs text-slate-400">{scopedPool.length}</span>
            </button>
            {availableTopics.map((t) => (
              <button
                key={t.name}
                onClick={() => toggleTopic(t.name)}
                className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border-2 transition-all active:scale-[0.98] ${selectedTopics.has(t.name) ? 'border-squishy-primary bg-teal-50 text-squishy-primary' : 'border-slate-200 text-slate-600'}`}
              >
                <span className="font-medium text-left text-sm">{t.name}</span>
                <span className="text-xs text-slate-400 shrink-0 ml-2">{t.count}</span>
              </button>
            ))}
          </div>

          {/* Count */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">Questions</span>
              <span className="text-sm font-bold text-squishy-primary">{effectiveCount} <span className="text-xs font-normal text-slate-400">/ {available}</span></span>
            </div>
            <input
              type="range"
              min={5}
              max={Math.max(5, maxQuestions)}
              step={1}
              value={effectiveCount}
              onChange={(e) => setQuestionCount(Number(e.target.value))}
              disabled={available < 5}
              className="w-full accent-squishy-primary"
            />
          </div>

          {available < 5 && (
            <p className="text-xs text-red-500">Not enough questions in this selection (need at least 5). Try All or a different filter.</p>
          )}
          {createMutation.isError && (
            <p className="text-xs text-red-500">{(createMutation.error as Error)?.message || 'Failed to create group.'}</p>
          )}

          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending || available < 5}
            className="w-full bg-squishy-primary hover:bg-teal-600 text-white font-semibold py-3 rounded-xl disabled:opacity-50 transition-all"
          >
            {createMutation.isPending ? 'Creating...' : `Create Group (${effectiveCount} questions)`}
          </button>
        </div>
      )}

      {/* Friends' decks upsell (Plus+) */}
      {showFriendsUpsell && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => setShowFriendsUpsell(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white rounded-2xl w-full max-w-sm p-5 text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-amber-500">
                <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-squishy-dark">Use your friends' questions</h3>
            <p className="mt-2 text-sm text-slate-600">
              Build a study group from the questions your friends uploaded. This is a <span className="font-semibold">Plus &amp; Pro</span> feature.
            </p>
            <button
              onClick={() => { setShowFriendsUpsell(false); navigate('/profile'); }}
              className="mt-5 block w-full rounded-xl bg-squishy-primary py-3 text-sm font-semibold text-white"
            >
              Upgrade to Plus (from $1.99/mo)
            </button>
            <button onClick={() => setShowFriendsUpsell(false)} className="mt-2 w-full py-2 text-sm text-slate-400">
              Maybe later
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChoiceCard({ title, subtitle, emoji, active, accent, onClick }: {
  title: string; subtitle: string; emoji: string; active: boolean;
  accent: 'amber' | 'teal' | 'red'; onClick: () => void;
}) {
  const ring = accent === 'amber' ? 'hover:border-amber-400' : accent === 'red' ? 'hover:border-red-400' : 'hover:border-squishy-primary';
  return (
    <button
      onClick={onClick}
      className={`w-full bg-white border-2 rounded-2xl p-4 text-left transition-all active:scale-[0.98] ${active ? 'border-squishy-primary ring-1 ring-squishy-primary/30' : `border-slate-200 ${ring}`}`}
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl leading-none mt-0.5">{emoji}</div>
        <div className="flex-1">
          <div className="text-sm font-bold text-squishy-dark">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{subtitle}</div>
        </div>
        <svg className="w-4 h-4 text-slate-400 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
}
